<script lang="ts">
import "@cisco-u/icons/icons/cu-icon-chevron-right.js";
import "@cisco-u/navigation-buttons/cu-navigation-buttons.js";
import "@cisco-u/pagination/cu-pagination.js";
import "@cisco-u/promo-card/cu-promo-card.js";
import "@cisco-u/carousel/cu-carousel.js";

import type { PropType } from "vue";
import { defineComponent } from "vue";
import { mapGetters } from "vuex";

import type { Result } from "@/components/Result";
import theCards from "@/components/theCards.vue";
import isUnauth from "@/utils/isUnauth";
import telemetry from "@/utils/telemetry";

const learningPathResult = "learning-path-result";
interface PromoResult {
  type: string;
}

// create union type for PromoResult and Result
type ResultUnion = Result | PromoResult;

export default defineComponent({
  components: {
    theCards,
  },

  props: {
    results: {
      type: Array as PropType<Result[]>,
      default: () => [],
    },
    resultComponent: {
      type: String,
      required: true,
    },
    viewAllText: {
      type: String,
      default: "",
    },
    exploreAllText: {
      type: String,
      default: "",
    },
    fetched: {
      type: Boolean,
      default: false,
    },
    viewAllLink: {
      type: Object,
      default: () => ({}),
    },
    hideViewAll: {
      type: Boolean,
      default: false,
    },
    paddingTop: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      selected: 0,
      showOptions: false,
      focusedItem: 0,
      vssWidth: 0,
    };
  },

  computed: {
    ...mapGetters({
      status: "user/status",
    }),
    slotText(): string {
      const vnodes = this.$slots.default?.();
      if (!vnodes) return "";

      const extractText = (vnode: any): string => {
        if (typeof vnode.children === "string") {
          return vnode.children;
        }
        if (Array.isArray(vnode.children)) {
          return vnode.children.map(extractText).join("");
        }
        if (vnode.children && typeof vnode.children === "object") {
          return extractText(vnode.children);
        }
        return "";
      };

      return vnodes.map(extractText).join("").trim();
    },
    isDesktop(): boolean {
      return this.vssWidth >= 1440;
    },
    multiResults(): boolean {
      return this.results?.length > 0 && "results" in this.results[0];
    },
    selectedResults(): ResultUnion[] {
      let returnResults: ResultUnion[] = this.results;
      if (this.multiResults) {
        if (this.resultComponent == learningPathResult) {
          // @ts-ignore
          return returnResults[this.selected]?.tracks;
        }
        // @ts-ignore
        return returnResults[this.selected]["results"];
      }

      if (this.isPromo) {
        // insert promoResult every 5th card
        let promoIndex = 0;
        returnResults = returnResults.reduce((acc: any, result: any) => {
          if (promoIndex % 5 === 0) {
            acc.push({ type: "promo" });
          }
          acc.push(result);
          promoIndex++;
          return acc;
        }, []);
      }
      return returnResults;
    },
    slidesPerGroup(): number {
      let rtn = 0;
      switch (this.resultComponent) {
        case learningPathResult:
        case "tutorial-result":
        case "instructor-led-result":
          rtn = this.returnLargeCardGroup();
          break;
        case "cert-card":
        case "labs-card":
        case "videos-card":
        case "pod-cast-card":
        case "event-result":
        case "challenge-result":
        case "exam-voucher-card":
        case "practice-exam":
        case "practice-exam-result":
        case "bundle-card":
          rtn = this.returnSmallCardGroup();
          break;
      }
      return rtn;
    },
    isExplorePage(): boolean {
      return this.routePath().includes("/explore");
    },
    isCertificationPage(): boolean {
      return this.routePath().includes("/certification");
    },
    isPodcastPage(): boolean {
      return this.routePath().includes("/podcast/");
    },
    isForYouPage(): boolean {
      return this.routePath().includes("/for-you");
    },
    isCommunityLandingPage(): boolean {
      return this.routePath().includes("/communities");
    },
    isMyCommunityPage(): boolean {
      return this.routePath().includes("/my-learning/community");
    },
    noResultsText(): string {
      let start = `There are currently no ${this.exploreAllText}`;
      if (this.isForYouPage) {
        if (!this.fetched) {
          return "Loading recommendations";
        }
        start = start.slice(0, start.length - 1);
        start = `${start} recommendations`;
      }
      return `${start}`;
    },
    isPromo(): boolean {
      // assuming only promos on ForYou page
      return (
        this.isForYouPage &&
        this.promoComponents().includes(this.resultComponent)
      );
    },
  },

  methods: {
    isUnauth,
    promoComponents(): string[] {
      return [learningPathResult];
    },
    returnLargeCardGroup() {
      if (this.isExplorePage && this.vssWidth >= 1800) {
        return 3;
      } else if (this.vssWidth >= 1440) {
        return this.isExplorePage ? 2 : 3;
      } else if (this.vssWidth >= 896) {
        return this.isExplorePage ? 1 : 2;
      }
      return 1;
    },
    returnSmallCardGroup() {
      if (this.isExplorePage) {
        return this.returnSmallCardGroupExplore();
      } else if (this.vssWidth >= 1536) {
        return 4;
      } else if (this.vssWidth >= 1104) {
        return 3;
      } else if (this.vssWidth >= 672) {
        return 2;
      }
      return 1;
    },
    returnSmallCardGroupExplore() {
      if (this.vssWidth >= 1776) {
        return 4;
      } else if (this.vssWidth >= 1536) {
        return 3;
      } else if (this.vssWidth >= 1052) {
        return 2;
      }
      return 1;
    },
    correctData(result: Result) {
      if (result.catalog_data) {
        if (result.RelevancyScore) {
          result.catalog_data.relevance = result.RelevancyScore;
        } else if (result.Confidence) {
          result.catalog_data.relevance = result.Confidence;
        }
        return result.catalog_data;
      }
      return result;
    },

    backtoStart(x: any) {
      x.slideTo(0);
    },

    routePath(): string {
      return this?.$route?.path ? this?.$route?.path : "";
    },

    onSlideChange() {
      const titles = {
        "learning-path-result": "Learning Paths",
        "cert-card": "Explore Cisco's Courses",
        "labs-card": "Explore Cisco's Labs",
        "videos-card": "Recommended Videos",
        "pod-cast-card": "Top Cisco U. Podcasts",
        "event-result": "Events",
        "instructor-led-result": "Instructor-Led Training",
        "practice-exam-result": "Practice Exam",
        "challenge-result": "Challenges",
        "tutorial-result": "Tutorials",
        "exam-voucher-card": "Exam Vouchers",
        "bundle-card": "Bundles",
      };

      const title = titles[this.resultComponent as keyof typeof titles];

      telemetry.carousel_change(
        "carousel-change", // ok
        title, //ok
        window.btoa(title), // ok
        window.location.href,
        this.isUnauth()
      );
    },
    getWindowSize() {
      this.vssWidth = window.innerWidth;
    },
  },
  mounted() {
    this.getWindowSize();
    window.addEventListener("resize", this.getWindowSize);
  },
  unmounted() {
    window.removeEventListener("resize", this.getWindowSize);
  },
});
</script>

<template>
  <div
    :class="[
      'relative',
      {
        'md:min-h-[15.5rem]': results && results.length > 0,
        'pb-20': isPodcastPage,
        'pt-10 xxl:pt-[3.75rem]': paddingTop,
      },
    ]"
  >
    <cu-carousel
      :title="slotText"
      :pagination="selectedResults.length > slidesPerGroup && !hideViewAll"
      :link="$router.resolve(viewAllLink).href"
      :link-text="viewAllText"
    >
      <template v-for="(result, index) in selectedResults" :key="index">
        <div slot="items" v-if="result.type !== 'promo'">
          <the-cards
            :for-carousel="true"
            :result="correctData(result as Result)"
            :status="status"
            :show-description="false"
            tabindex="0"
            :index="index"
            card-view="List"
            card-style="Grid"
          />
        </div>
        <div
          style="--card-width: 18rem; --card-min-width: 18rem"
          slot="items"
          v-else
        >
          <cu-promo-card
            heading="Elevate your skills"
            description="Want to stand out in tech? Cisco Certifications make all the difference."
            btntext="Learn more"
            btnLink="/certifications"
            theme="dark"
            class="h-full min-h-[22rem]"
          />
        </div>
      </template>
    </cu-carousel>
  </div>
</template>
